import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { apiClient } from '../../../api/axios';

const fetchEventDetails = async ({ queryKey }) => {
  const [, { eventId }] = queryKey;
  const { data } = await apiClient.get(`/events/${eventId}`);
  // API returns { event: {...}, request_payload: {...} }
  // Return normalized structure with event and request_payload
  return {
    event: data.event || data,
    request_payload: data.request_payload,
  };
};

/**
 * Hook to fetch event details by ID
 * @param {string} eventId - The event ID to fetch
 * @returns {Object} Event details data and loading/error states
 */
export const useGetEventDetailsHandler = ({ eventId }) => {
  const dispatch = useDispatch();

  const {
    data: eventDetails,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['eventDetails', { eventId }],
    queryFn: fetchEventDetails,
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Handle errors
  useEffect(() => {
    if (isError && error instanceof AxiosError) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch event details';
      if (error.response?.status !== 404) {
        // Don't show error for 404, let the component handle it
        dispatch(showSnackbar({ message: errorMessage, severity: 'error' }));
      }
    }
  }, [isError, error, dispatch]);

  return {
    eventDetails,
    isLoading,
    isError,
    error,
    refetch,
  };
};
